import { defineStore } from 'pinia';
import { computed, readonly, ref, unref } from 'vue';

import wiring, { WIRING_STATUS } from '@/composables/wiring';

export { WIRING_STATUS };

const emptySpeedtestMeta = () => ({ requestedAtMs: null, lastAgeS: null, running: false });

function hashStringToId(input) {
  const str = String(input ?? '');
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h) ^ str.charCodeAt(i);
  }
  return `msg_${(h >>> 0).toString(36)}`;
}

function normalizeMessageType(level) {
  const l = String(level || '').toLowerCase();
  if (l === 'error') return 'error';
  if (l === 'warn' || l === 'warning') return 'warn';
  if (l === 'success') return 'success';
  return 'info';
}

const useStateStore = defineStore('state', () => {
  const loaded = ref(false);
  const remoteState = ref({});
  const remoteStatePollingError = ref(null);

  const sessionId = ref(null);

  const speedtestMeta = ref(emptySpeedtestMeta());

  const state = computed(() => remoteState.value);

  const prodWiringFields = computed(() => remoteState.value?.data?.wiring?.fields?.production || {});
  const devWiringFields = computed(() => remoteState.value?.data?.wiring?.fields?.development || {});

  const {
    wiringDraftProduction,
    wiringDraftDevelopment,
    wiringDraftTouchedProduction,
    wiringDraftTouchedDevelopment,
    wiringValues,
    wiringProductionChanged,
    wiringDisplayRows,
    wiringDisplayMode,
    wiringHasUiOverrides,
    wiringHasChanges,
    syncWiringDraftFromRemote,
    clearWiringDraft,
    setWiringDraftValue,
    resetWiringDraftToDefaults,
  } = wiring(prodWiringFields, devWiringFields);

  const wanSpeedtestAgeS = computed(() => {
    const v = remoteState.value?.data?.wan?.speedtest?.age_s;
    return typeof v === 'number' ? v : null;
  });

  const remoteAlerts = computed(() => {
    const msgs = remoteState.value?.messages || [];
    if (!Array.isArray(msgs)) return [];

    return msgs
      .filter((m) => Boolean(m?.alert))
      .map((m) => {
        const level = String(m?.level || 'info');
        const title = m?.title != null && String(m.title).trim() ? String(m.title) : null;

        const text = m?.text != null ? String(m.text) : '';
        const code = m?.code ?? null;

        const signature = JSON.stringify({
          level,
          title: title || '',
          text,
          code,
        });

        return {
          id: hashStringToId(signature),
          type: normalizeMessageType(level),
          title,
          message: text || '-',
          detail: code,
          closable: m?.closeable !== false,
        };
      });
  });

  const transfersUi = computed(() => remoteState.value?.data?.transfers || {});

  function setSessionId(value) {
    const v = value != null ? String(value).trim() : '';
    sessionId.value = v ? v : null;
  }

  function updateRemoteState(newRemoteState) {
    newRemoteState = unref(newRemoteState);

    if (!newRemoteState) {
      remoteState.value = {};
      loaded.value = false;

      const sid = newRemoteState?.session_id ?? newRemoteState?.data?.session_id ?? null;
      sessionId.value = sid ? String(sid) : null;

      clearWiringDraft();
      speedtestMeta.value = emptySpeedtestMeta();
      return;
    }

    remoteState.value = newRemoteState;
    loaded.value = true;

    const sid = newRemoteState?.session_id ?? newRemoteState?.data?.session_id ?? null;
    sessionId.value = sid ? String(sid) : sessionId.value;

    syncWiringDraftFromRemote();
    syncSpeedtestFromRemote();
  }

  function updateRemoteStatePollingError(error) {
    remoteStatePollingError.value = error;
  }

  function markSpeedtestRequested() {
    const nowMs = Date.now();
    speedtestMeta.value = {
      requestedAtMs: nowMs,
      lastAgeS: wanSpeedtestAgeS.value,
      running: true,
    };
  }

  function syncSpeedtestFromRemote() {
    const prev = speedtestMeta.value || emptySpeedtestMeta();
    const ageS = wanSpeedtestAgeS.value;

    const ageIsNumber = typeof ageS === 'number';

    if (!prev.running) {
      speedtestMeta.value = {
        ...prev,
        ...(ageIsNumber ? { lastAgeS: ageS } : {}),
      };
      return;
    }

    if (!ageIsNumber) {
      if (prev.lastAgeS === null) {
        return;
      }
      speedtestMeta.value = { ...prev, running: false };
      return;
    }

    const prevAge = prev.lastAgeS;
    const finished = typeof prevAge === 'number' ? ageS < prevAge : ageS === 0;

    speedtestMeta.value = {
      ...prev,
      lastAgeS: ageS,
      running: !finished,
    };
  }

  const speedtestRunning = computed(() => Boolean(speedtestMeta.value?.running));
  const speedtestRequestedAtMs = computed(() => speedtestMeta.value?.requestedAtMs ?? null);

  return {
    loaded: readonly(loaded),
    remoteStatePollingError: readonly(remoteStatePollingError),

    sessionId: readonly(sessionId),
    setSessionId,

    state,
    remoteAlerts: readonly(remoteAlerts),

    wiringDraftProduction,
    wiringDraftDevelopment,

    wiringDraftTouchedProduction,
    wiringDraftTouchedDevelopment,

    wiringValues,
    wiringProductionChanged,

    wiringDisplayRows,
    wiringDisplayMode,
    wiringHasUiOverrides,
    wiringHasChanges,

    transfersUi,

    speedtestRunning,
    speedtestRequestedAtMs,
    markSpeedtestRequested,

    updateRemoteState,
    updateRemoteStatePollingError,
    setWiringDraftValue,
    resetWiringDraftToDefaults,
  };
});

export default useStateStore;
